
import React from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";

const PricingGuidePage: React.FC = () => {
  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold mb-6">Pokémon Card Pricing Guide</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Understanding what makes a Pokémon card valuable is essential for collectors. 
            This guide will help you identify the key factors that influence card prices.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader className="bg-gradient-to-r from-pokemon-blue/10 to-pokemon-blue/5">
                <CardTitle className="text-pokemon-blue">Rarity & Set Symbols</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-yellow-600 text-xl font-bold">★</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Rarity Matters</h3>
                    <p className="text-sm text-gray-600">Cards with rare symbols (star, holofoil) are generally more valuable</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm list-disc pl-5">
                  <li>Common (black circle)</li>
                  <li>Uncommon (black diamond)</li>
                  <li>Rare (black star)</li>
                  <li>Ultra Rare (holographic star)</li>
                  <li>Secret Rare (number exceeds set list)</li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card>
              <CardHeader className="bg-gradient-to-r from-pokemon-yellow/10 to-pokemon-yellow/5">
                <CardTitle className="text-pokemon-yellow">Card Condition</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 text-xl font-bold">PSA</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Grading Scale</h3>
                    <p className="text-sm text-gray-600">Professional grading significantly affects value</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li><span className="font-medium">PSA 10 (Gem Mint):</span> Perfect centering, corners, edges, surface</li>
                  <li><span className="font-medium">PSA 9 (Mint):</span> Nearly perfect with minor flaws</li>
                  <li><span className="font-medium">PSA 8 (Near Mint):</span> Slight imperfections visible on close inspection</li>
                  <li><span className="font-medium">PSA 7 and below:</span> Noticeable wear, decreasing value significantly</li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Special Card Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gradient-to-b from-purple-50 to-transparent rounded-lg border border-purple-100">
                  <h3 className="font-semibold text-purple-700">Full Art</h3>
                  <p className="text-sm">Artwork extends beyond the standard frame</p>
                </div>
                <div className="p-4 bg-gradient-to-b from-blue-50 to-transparent rounded-lg border border-blue-100">
                  <h3 className="font-semibold text-blue-700">Secret Rare</h3>
                  <p className="text-sm">Card numbers higher than the set number</p>
                </div>
                <div className="p-4 bg-gradient-to-b from-amber-50 to-transparent rounded-lg border border-amber-100">
                  <h3 className="font-semibold text-amber-700">Gold Star</h3>
                  <p className="text-sm">Extremely rare cards with a gold star symbol</p>
                </div>
                <div className="p-4 bg-gradient-to-b from-emerald-50 to-transparent rounded-lg border border-emerald-100">
                  <h3 className="font-semibold text-emerald-700">Ex/GX/V/VMAX</h3>
                  <p className="text-sm">Special cards with unique powers and mechanics</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Market Influences</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                <li className="flex">
                  <div className="mr-4 mt-1 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Age and Set</h3>
                    <p className="text-sm text-gray-600">Vintage cards, especially from the Base Set, Jungle, and Fossil sets (1999-2000) typically command higher prices.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-4 mt-1 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Popularity</h3>
                    <p className="text-sm text-gray-600">Cards featuring popular Pokémon like Pikachu, Charizard, and Mewtwo tend to be more valuable.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-4 mt-1 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Errors and Misprints</h3>
                    <p className="text-sm text-gray-600">Cards with printing errors or miscuts can be highly valuable to specific collectors.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-4 mt-1 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Tournament Viability</h3>
                    <p className="text-sm text-gray-600">Cards that perform well in competitive play often see temporary value increases.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageContainer>
  );
};

export default PricingGuidePage;
